import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk' }}>
              Terms of Service
            </CardTitle>
            <p className="text-sm text-gray-600">Last Updated: {new Date().toLocaleDateString()}</p>
          </CardHeader>
          <CardContent className="space-y-6 text-gray-700">
            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>1. Acceptance of Terms</h2>
              <p>By accessing and using the NextGen Trades Academy platform, you accept and agree to be bound by the terms and provisions of this agreement. This platform is operated by NextGen Trades Academy for educational and networking purposes.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>2. Use License</h2>
              <p>Permission is granted to use this platform for personal, non-commercial purposes related to job searching, networking, and community engagement within the NextGen Trades Academy community.</p>
              <p className="mt-2">You may not:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Use the platform for any illegal purpose</li>
                <li>Attempt to gain unauthorized access to any portion of the platform</li>
                <li>Interfere with or disrupt the platform's operation</li>
                <li>Transmit any viruses, malware, or harmful code</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>3. User Conduct and Community Guidelines</h2>
              <p>All users must comply with our Community Guidelines. Prohibited conduct includes but is not limited to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Harassment, bullying, or threatening behavior</li>
                <li>Discriminatory language or hate speech</li>
                <li>Sexual harassment or unwanted advances</li>
                <li>Sharing false or misleading information</li>
                <li>Spam or unsolicited commercial messages</li>
                <li>Impersonation of others</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>4. California Civil Code Section 1708.5</h2>
              <p>In compliance with California law, we do not tolerate harassment in any form. Harassment includes but is not limited to unwelcome sexual advances, requests for sexual favors, and other verbal, visual, or physical conduct of a sexual nature, as well as conduct based on race, religion, color, national origin, ancestry, physical or mental disability, medical condition, genetic information, marital status, sex, gender, gender identity, gender expression, age, sexual orientation, or military and veteran status.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>5. Reporting Violations</h2>
              <p>If you experience or witness harassment, inappropriate behavior, or violations of these terms, please report immediately to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Email: <a href="mailto:compliance@ngta.com" className="text-teal-600 underline">compliance@ngta.com</a></li>
                <li>Use the "Report" button available in messages and profiles</li>
              </ul>
              <p className="mt-2">All reports will be investigated promptly and confidentially.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>6. Account Termination</h2>
              <p>We reserve the right to terminate or suspend access to our platform immediately, without prior notice or liability, for any reason, including without limitation if you breach the Terms of Service.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>7. Disclaimer of Warranties</h2>
              <p>THIS PLATFORM IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. NEXTGEN TRADES ACADEMY MAKES NO REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO THE OPERATION OF THE PLATFORM OR THE INFORMATION, CONTENT, OR MATERIALS INCLUDED.</p>
              <p className="mt-2"><strong>Job Listings Disclaimer:</strong> NextGen Trades Academy is not an employment agency. We do not guarantee job placement. Job listings are provided by third-party employers. We are not responsible for the accuracy of job postings or the conduct of employers.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>8. Limitation of Liability</h2>
              <p>TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, NEXTGEN TRADES ACADEMY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.</p>
              <p className="mt-2">NextGen Trades Academy is not responsible for interactions, disputes, or issues arising between users, including but not limited to employment disputes, contract disputes, or personal conflicts.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>9. California Consumer Privacy Rights</h2>
              <p>California residents have specific rights regarding their personal information under the California Consumer Privacy Act (CCPA). Please see our Privacy Policy for details on how we collect, use, and protect your data, and your rights under California law.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>10. Modifications</h2>
              <p>We reserve the right to modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the modified terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>11. Governing Law</h2>
              <p>These terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>12. Contact Information</h2>
              <p>For questions about these Terms of Service:</p>
              <ul className="mt-2 space-y-1">
                <li>Email: <a href="mailto:legal@ngta.com" className="text-teal-600 underline">legal@ngta.com</a></li>
                <li>Compliance Issues: <a href="mailto:compliance@ngta.com" className="text-teal-600 underline">compliance@ngta.com</a></li>
              </ul>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}